import { useQuery } from '@tanstack/react-query';

export class ApiError extends Error {
  constructor(message: string, public status: number, public details?: unknown) { super(message); }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin', ...options,
    headers: { ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...options.headers },
  });
  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const error = body as { error?: { message?: string; details?: unknown } | string } | null;
    const message = typeof error?.error === 'string' ? error.error : error?.error?.message;
    throw new ApiError(message ?? (response.status === 429 ? 'Too many requests. Wait a moment and try again.' : response.status === 401 ? 'Your session has ended. Sign in again to continue.' : `The request could not be completed (${response.status}). Try again.`), response.status, error);
  }
  return body as T;
}

export function post<T>(path: string, body: unknown = {}): Promise<T> {
  return api<T>(path, { method: 'POST', body: JSON.stringify(body) });
}
export function patch<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}
export function useApi<T>(path: string, options: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({ queryKey: [path], queryFn: () => api<T>(path), ...options });
}

export interface ReferenceItem {
  id: string; name?: string; symbol?: string; ticker?: string; portfolioId?: string;
  subfolioId?: string; securityId?: string; description?: string;
  currency?: string;
}
export interface ReferenceData {
  portfolios: ReferenceItem[]; subfolios: ReferenceItem[];
  securities: ReferenceItem[]; registrations: ReferenceItem[];
}
export const referenceLabel = (item: ReferenceItem) => item.symbol ? `${item.symbol} · ${item.name ?? ''}` : item.name ?? item.ticker ?? item.id;
export const errorMessage = (error: unknown) => error instanceof TypeError && /fetch|network/i.test(error.message) ? 'The server could not be reached. Check your connection and try again.' : error instanceof Error ? error.message : 'An unexpected error occurred';
